
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner';
import OrganizationLogo from '../OrganizationLogo';
import { LanguageSwitcher } from '../Layout/LanguageSwitcher';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft } from 'lucide-react';

const DEMO_EMAIL = 'demo@sme.ng';
const DEMO_PASSWORD = 'demo123';
// Removed hardcoded admin credentials for security

const LOGIN_ERROR_KEY = 'login_error_temp';
// The seeded Fumba demo org - matched by ID, not by name substring. A name
// match (organizationName.includes('fumba')) would also expose the plaintext
// demo password and quick-login shortcuts on any FUTURE real organization
// whose name happens to contain "fumba".
const FUMBA_DEMO_ORG_ID = '00000000-0000-0000-0000-000000000001';

// Client-side-only lockout feedback. Supabase enforces the REAL rate limit
// server-side regardless of this - this purely gives the user a clear "wait
// a bit" message instead of letting them keep hammering Sign In and only
// ever seeing a generic failure once Supabase's own limit kicks in.
const FAILED_ATTEMPTS_KEY = 'login_failed_attempts';
const LOCKOUT_UNTIL_KEY = 'login_lockout_until';
const LOCKOUT_THRESHOLD = 5;
const LOCKOUT_DURATION_MS = 30_000;

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Initialize error from sessionStorage to survive remounts
  const [error, setError] = useState(() => {
    const stored = sessionStorage.getItem(LOGIN_ERROR_KEY);
    return stored || '';
  });
  const [demoLoading, setDemoLoading] = useState(false);
  const [organizationName, setOrganizationName] = useState('Growth Energy');
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  const isDemoOrg = localStorage.getItem('organizationId') === FUMBA_DEMO_ORG_ID;
  const [lockedUntil, setLockedUntil] = useState(() => Number(sessionStorage.getItem(LOCKOUT_UNTIL_KEY) || 0));
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const { login, isLoading } = useAuth();

  // Custom setError that persists to sessionStorage
  const setErrorPersistent = (msg: string) => {
    setError(msg);
    if (msg) {
      sessionStorage.setItem(LOGIN_ERROR_KEY, msg);
    } else {
      sessionStorage.removeItem(LOGIN_ERROR_KEY);
    }
  };

  // Tick the lockout countdown once a second; clears itself once expired.
  useEffect(() => {
    if (!lockedUntil) { setCooldownSeconds(0); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setCooldownSeconds(remaining);
      if (remaining === 0) {
        setLockedUntil(0);
        sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
        sessionStorage.removeItem(FAILED_ATTEMPTS_KEY);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const recordFailedAttempt = () => {
    const attempts = Number(sessionStorage.getItem(FAILED_ATTEMPTS_KEY) || 0) + 1;
    if (attempts >= LOCKOUT_THRESHOLD) {
      const until = Date.now() + LOCKOUT_DURATION_MS;
      sessionStorage.setItem(LOCKOUT_UNTIL_KEY, String(until));
      sessionStorage.setItem(FAILED_ATTEMPTS_KEY, '0');
      setLockedUntil(until);
    } else {
      sessionStorage.setItem(FAILED_ATTEMPTS_KEY, String(attempts));
    }
  };

  const clearFailedAttempts = () => {
    sessionStorage.removeItem(FAILED_ATTEMPTS_KEY);
    sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
    setLockedUntil(0);
  };

  useEffect(() => {
    const fetchOrganization = async () => {
      const orgId = localStorage.getItem('organizationId');
      if (orgId) {
        const { supabase } = await import('@/integrations/supabase/client');

        const { data, error } = await supabase
          .from('organizations')
          .select('name, logo_url')
          .eq('id', orgId)
          .single();

        if (!error && data) {
          setOrganizationName(data.name);
          setOrganizationLogo(data.logo_url);
        }
      }
    };

    fetchOrganization();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorPersistent('');

    if (cooldownSeconds > 0) {
      setErrorPersistent(t('auth.tooManyLoginAttemptsWait', { seconds: cooldownSeconds }));
      return;
    }

    // Trim first - a copy-pasted email/password with stray whitespace would
    // otherwise reach Supabase as a genuinely different credential and fail
    // with the generic "Invalid email or password" instead of ever being
    // caught as a formatting issue.
    const trimmedEmail = email.trim();

    // Client-side validation
    if (!trimmedEmail || !password) {
      setErrorPersistent(t('auth.fillAllFields'));
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setErrorPersistent(t('auth.invalidEmail'));
      return;
    }

    const result = await login(trimmedEmail, password);

    if (!result || !result.success) {
      const errorMsg = result?.error || t('auth.loginFailed');
      setErrorPersistent(errorMsg);
      recordFailedAttempt();
    } else {
      // Clear error on success
      setErrorPersistent('');
      clearFailedAttempts();
    }
  };

  const handleDemoLogin = async () => {
    setErrorPersistent('');
    setDemoLoading(true);
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    const result = await login(DEMO_EMAIL, DEMO_PASSWORD);
    if (!result.success) {
      setErrorPersistent(result.error || t('auth.demoLoginFailed'));
    } else {
      setErrorPersistent('');
    }
    setDemoLoading(false);
  };

  const handleAdminLogin = () => {
    setErrorPersistent('');
    // Unlike Demo (which logs in immediately), this deliberately does NOT
    // auto-login - admin users must manually enter their real password for
    // security. Without a clear signal, clicking this button looked
    // indistinguishable from a dead/broken button: no loading state, no
    // redirect, nothing visibly happens. Shifting focus into the password
    // field makes it obvious the email was filled and a password is now
    // expected, matching the standard "next step" affordance.
    setEmail('admin@growth-energy.tz');
    setPassword('');
    passwordInputRef.current?.focus();
  };

  const showLoading = isLoading || demoLoading;
  const isLockedOut = cooldownSeconds > 0;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-primary/5 to-secondary/10 dark:from-background dark:via-primary/10 dark:to-secondary/5">
      <div className="w-full max-w-md space-y-8 animate-in fade-in-0 duration-700">
        {/* Top bar: back + language switcher */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/preauth')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t('auth.backToOrganizations')}</span>
          </button>
          <LanguageSwitcher />
        </div>

        {/* Header */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="relative group">
              <div className="transition-all duration-300 group-hover:scale-105">
                <OrganizationLogo
                  logoUrl={organizationLogo}
                  organizationName={organizationName}
                  size="lg"
                  className="shadow-2xl"
                />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-br from-primary to-primary/60 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-3xl animate-pulse"></div>
            </div>
          </div>
          
          <div className="space-y-3">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">{t('auth.welcomeBack')}</h1>
            <p className="text-muted-foreground text-lg">{t('auth.platformSubtitle')}</p>
            {organizationName && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 text-primary text-sm font-medium shadow-sm">
                <div className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></div>
                {organizationName}
              </div>
            )}
          </div>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl bg-card/95 backdrop-blur-lg border border-border/50 animate-in slide-in-from-bottom-4 duration-700 delay-200">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div role="alert" aria-live="assertive" className="p-4 text-sm text-destructive bg-transparent border border-destructive/30 rounded-xl">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  {t('auth.emailAddress')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 px-4 bg-background/50 border-border focus:bg-background focus:border-primary focus:ring-primary/20 rounded-xl transition-all duration-200"
                  placeholder={t('auth.emailPlaceholder')}
                  disabled={showLoading || isLockedOut}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    {t('auth.password')}
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <Input
                  id="password"
                  ref={passwordInputRef}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 px-4 bg-background/50 border-border focus:bg-background focus:border-primary focus:ring-primary/20 rounded-xl transition-all duration-200"
                  placeholder={t('auth.passwordPlaceholder')}
                  disabled={showLoading || isLockedOut}
                />
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={showLoading || isLockedOut}
                  className="w-full h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-primary/25"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>{t('auth.signingIn')}</span>
                    </div>
                  ) : isLockedOut ? (
                    t('auth.tooManyLoginAttemptsWait', { seconds: cooldownSeconds })
                  ) : (
                    t('auth.signIn')
                  )}
                </Button>

                {/* Quick Access - Only shown for Fumba Demo organization */}
                {isDemoOrg && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-card text-muted-foreground">{t('auth.quickAccess')}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        onClick={handleDemoLogin}
                        disabled={showLoading}
                        variant="outline"
                        className="h-12 border-2 border-secondary/40 text-secondary-foreground hover:bg-secondary/20 hover:border-secondary/60 rounded-xl transition-all duration-200 group"
                      >
                        {demoLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <span className="group-hover:scale-105 transition-transform duration-200">{t('auth.demo')}</span>
                        )}
                      </Button>

                      <Button
                        type="button"
                        onClick={handleAdminLogin}
                        disabled={showLoading}
                        variant="outline"
                        className="h-12 border-2 border-accent/40 text-accent-foreground hover:bg-accent/20 hover:border-accent/60 rounded-xl transition-all duration-200 group"
                      >
                        <span className="group-hover:scale-105 transition-transform duration-200">{t('auth.adminLogin')}</span>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Demo Credentials - Only shown for Fumba Demo organization */}
        {isDemoOrg && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs animate-in fade-in-0 duration-700 delay-500">
            <div className="p-4 bg-secondary/10 border border-secondary/20 rounded-xl backdrop-blur-sm hover:bg-secondary/15 transition-colors duration-200">
              <h4 className="font-medium text-secondary-foreground mb-2 flex items-center">
                <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                {t('auth.demoAccount')}
              </h4>
              <div className="text-muted-foreground space-y-1">
                <div>Email: demo@sme.ng</div>
                <div>Password: demo123</div>
              </div>
            </div>
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-xl backdrop-blur-sm hover:bg-accent/15 transition-colors duration-200">
              <h4 className="font-medium text-accent-foreground mb-2 flex items-center">
                <div className="w-2 h-2 bg-accent rounded-full mr-2"></div>
                {t('auth.administrator')}
              </h4>
              <div className="text-muted-foreground space-y-1">
                <div>{t('auth.contactAdminForCredentials')}</div>
                <div>{t('auth.secureAuthRequired')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-center space-y-3 animate-in fade-in-0 duration-700 delay-600">
          <div className="p-4 bg-muted/50 border border-border/50 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-muted-foreground">
              <strong>{t('auth.noAccountYet')}</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('auth.contactOrgAdmin')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center animate-in fade-in-0 duration-700 delay-700">
          <p className="text-xs text-muted-foreground">
            {t('auth.poweredBy')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
